import { Prose } from '../../components/Prose'
import { hasBabyPhase, type Profile } from '../../lib/metrics'
import { useSaveTemplate, useSchedule, useClearOverride } from '../../api/hooks'
import {
  DEFAULT_SETTINGS,
  KIND_LABEL,
  WEEKDAY_NAMES,
  addDays,
  fullTemplate,
  planForDate,
  setTemplateDay,
  weekStart,
  weekSummary,
  type ActivityKind,
  type TemplateDay,
  type ScheduleSettings,
} from '../../lib/schedule'
import { todayIso, trDate } from '../../lib/format'
import { ACTIVITIES } from '../../lib/energy'

const KINDS: ActivityKind[] = ['strength', 'sport', 'prep', 'rest']
/** Şablonda seçilebilecek sporlar — katalogdan, gruplu. */
const SPORT_GROUPS = [...new Set(ACTIVITIES.map((a) => a.group))]

export function WeekTab({ profile }: { profile: Profile }) {
  const sched = useSchedule()
  const saveTemplate = useSaveTemplate()
  const clearOverride = useClearOverride()

  if (!sched.data) return <div className="card"><p className="sub">Program yükleniyor…</p></div>

  const settings: ScheduleSettings = {
    ...DEFAULT_SETTINGS,
    strengthPerWeek: profile.strengthPerWeek ?? DEFAULT_SETTINGS.strengthPerWeek,
    makeupWeekday: profile.makeupWeekday ?? DEFAULT_SETTINGS.makeupWeekday,
    makeupTime: (profile.makeupTime ?? DEFAULT_SETTINGS.makeupTime).slice(0, 5),
    preTennisMinutes: profile.preTennisMinutes ?? DEFAULT_SETTINGS.preTennisMinutes,
    sessionMinutes: profile.sessionMinutes ?? DEFAULT_SETTINGS.sessionMinutes,
    postTennisMinutes: profile.postTennisMinutes ?? DEFAULT_SETTINGS.postTennisMinutes,
  }

  // Uyarı metnindeki kilo profilden; girilmemişse cümle kilosuz kuruluyor.
  const agirlikNotu = profile.startWeightKg >= 30
    ? ` (${Math.round(profile.startWeightKg)} kg'da fazlasıyla)` : ''

  const { template, overrides, strengthDoneDates } = sched.data
  const base = { meal1: profile.meal1, meal2: profile.meal2, kitchenClose: profile.kitchenClose }
  const today = todayIso()
  const start = weekStart(today)
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i))
  const week = weekSummary(today, template, overrides, strengthDoneDates, settings)

  // Eksik günü de yazabiliyor: eskiden `template.map` ile yapılıyordu ve
  // şablonda satırı olmayan günü değiştirmek sessizce hiçbir şey yapmıyordu.
  const setDay = (weekday: number, patch: Partial<TemplateDay>) =>
    saveTemplate.mutate(setTemplateDay(template, weekday, patch))

  // Kaç kuvvet günü hâlâ önümüzde? Bunu tahmin etmek yerine sayıyoruz.
  let strengthCount = strengthDoneDates.length

  return (
    <>
      <div className="card">
        <h2>Bu hafta</h2>
        <Prose className="card-h">
          {trDate(start)} – {trDate(days[6])} · her günün planı şablondan türetilir,
          program dışı günler işaretli.
        </Prose>
        <div className="grid g2">
          {days.map((d) => {
            const plan = planForDate(d, template, overrides, base, strengthCount, settings)
            if (plan.kind === 'strength' && d < today) strengthCount++ // geçmiş günler rotasyonu ilerletir
            const isToday = d === today
            return (
              <div
                className="day"
                key={d}
                style={{
                  borderLeft: `3px solid ${
                    plan.kind === 'sport' ? 'var(--s2)' : plan.kind === 'strength' ? 'var(--s1)' : 'var(--border)'
                  }`,
                  opacity: d < today ? 0.62 : 1,
                  outline: isToday ? '1px solid var(--s1)' : undefined,
                }}
              >
                <div className="dn">
                  {WEEKDAY_NAMES[plan.weekday]} {trDate(d).slice(0, 5)}
                  {isToday && ' · bugün'}
                  {plan.isOverride && <span style={{ color: 'var(--s2)' }}> · program dışı</span>}
                </div>
                <div className="dt">{plan.label}</div>
                <div className="dd">
                  Öğünler {plan.meals.meal1} · {plan.meals.preSnack ? `${plan.meals.preSnack} ara · ` : ''}
                  {plan.meals.meal2} · mutfak {plan.meals.kitchenClose}
                </div>
                {plan.isOverride && (
                  <button style={{ marginTop: 8, padding: '3px 9px', fontSize: 12 }}
                          onClick={() => clearOverride.mutate(d)}>
                    istisnayı kaldır
                  </button>
                )}
              </div>
            )
          })}
        </div>

        <div className="warnbox" style={{ borderLeftColor: week.unreachable ? 'var(--crit)' : 'var(--warn)' }}>
          <strong>Kuvvet:</strong> {week.strengthCompleted}/{week.strengthTarget} tamam ·
          programda {week.strengthPlanned} gün · <strong>Spor:</strong> {week.sportPlanned} gün
          {week.makeupDate && <> · telafi: {WEEKDAY_NAMES[settings.makeupWeekday]} {week.makeupTime}</>}
          {week.unreachable && <> · <strong style={{ color: 'var(--crit)' }}>bu hafta hedefe ulaşılamıyor</strong></>}
        </div>
      </div>

      <div className="card">
        <h2>Haftalık şablon</h2>
        <Prose className="card-h">
          Normalde hangi gün ne yaparsın — <strong>yedi günün hepsini buradan
          değiştirebilirsin</strong>. Spor seçersen listedeki her aktiviteyi
          kullanabilirsin, tenis burada özel bir şey değil. Tek seferlik
          değişiklikler için şablonu değiştirme, Takip sekmesindeki gün
          düğmelerini kullan.
        </Prose>
        <table>
          <thead><tr><th>Gün</th><th>Ne</th><th>Aktivite</th><th>Saat</th><th>Süre</th></tr></thead>
          <tbody>
            {/* fullTemplate: eksik günler `rest` olarak tamamlanıyor. Önce
                "satırı yoksa çizme" deniyordu ve Salı/Perşembe/Cumartesi/Pazar
                tabloda hiç görünmüyordu — o günlere bir şey atamak imkânsızdı. */}
            {fullTemplate(template).map((t) => {
              const wd = t.weekday
              return (
                <tr key={wd}>
                  <td style={{ fontVariantNumeric: 'normal' }}>{WEEKDAY_NAMES[wd]}</td>
                  <td>
                    <select value={t.kind} onChange={(e) => setDay(wd, { kind: e.target.value as ActivityKind })}>
                      {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                    </select>
                  </td>
                  <td>
                    {t.kind === 'sport'
                      ? <select value={t.activityKey ?? 'tennis_singles'}
                                onChange={(e) => setDay(wd, { activityKey: e.target.value })}>
                          {SPORT_GROUPS.map((g) => (
                            <optgroup key={g} label={g}>
                              {ACTIVITIES.filter((a) => a.group === g).map((a) => (
                                <option key={a.key} value={a.key}>{a.name}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td>
                    {t.kind === 'sport'
                      ? <input type="time" defaultValue={(t.startTime ?? '20:30').slice(0, 5)}
                               onBlur={(e) => setDay(wd, { startTime: e.target.value })} />
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td>
                    {t.kind === 'sport'
                      ? <input type="number" min={10} max={300} style={{ width: 80 }}
                               defaultValue={t.minutes ?? settings.sessionMinutes}
                               onBlur={(e) => setDay(wd, { minutes: +e.target.value || null })} />
                      : <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <Prose className="note">
          Spor günü seçtiğinde öğün penceresi otomatik kayar: seans saatinden{' '}
          {Math.round(settings.preTennisMinutes / 60 * 10) / 10} saat önce ara öğün, seans sonrası 2. öğün.
        </Prose>
      </div>

      {/* Bu kart eskiden "Tenis günü nasıl kurgulanır" idi ve saatleri sabitti
          (12:00 öğün, 115 kg uyarısı) — yani tek bir kişinin haftasıydı. Artık
          hem spor türünden bağımsız hem de saatler/kilo profilden geliyor. */}
      <div className="card">
        <h2>Spor günü nasıl kurgulanır</h2>
        <ul className="tight">
          <li><strong>{profile.meal1.slice(0, 5)}</strong> — 1. öğün, günün büyük öğünü. Karbonhidratı tam çukur avuç al.</li>
          <li><strong>Seans − {Math.round(settings.preTennisMinutes / 60 * 10) / 10} saat</strong> — Küçük ara öğün: 1 muz + 1 kase yoğurt (~200 kcal). Aç antrenmana gitme.</li>
          <li><strong>Seans − 15 dk</strong> — Isınma: hafif koşu, kol çevirme, yana adım. Soğuk kasla ani hareket = omuz ve diz sakatlığı.</li>
          <li><strong>Seans + {settings.postTennisMinutes} dk</strong> — 2. öğün. Protein ağırlıklı, hafif. <strong>Pilav/makarna/pide yok.</strong></li>
          <li><strong>Su</strong> — Seans boyunca 500–750 ml. Ertesi sabah tartıda +1 kg görürsen bu kas su tutması.</li>
        </ul>
        <div className="warnbox">
          Haftada 3+ gün yön değiştirmeli spor (tenis, futbol, basketbol) oynamaya başlarsan dize dikkat
          {agirlikNotu} — yana hareket ve ani duruş eklem için ciddi yük. Diz ya da aşil ağrısı
          başlarsa sporu değil, o hafta kuvvet gününü kes.
        </div>
      </div>

      {/* Hamilelik bölümü yalnızca doğum tarihi girenlere. */}
      {hasBabyPhase(profile) && (
      <div className="card">
        <h2>Eşin hamileyken bu iş nasıl yürür</h2>
        <ul className="tight">
          <li><strong>Yürüyüşü ortak yapın.</strong> Normal seyreden gebelikte tempolu yürüyüş genelde önerilir — doktoru onay verirse akşam 30–40 dk beraber yürümek ikinize de yarar.</li>
          <li><strong>Yemek hazırlığını birlikte yapın.</strong> Pazar 1 saat: tavuk pişir, bulguru haşla, sebzeleri doğra. "Ne yesek" sorusu siparişle biter — o soruyu hiç sorma.</li>
          <li><strong>Onun antojularını kendi test alanın yapma.</strong> Eve gelen dondurma onun; senin payın yok. Bunu baştan konuşun.</li>
          <li><strong>Ekstra spor günlerini önceden haber ver.</strong> Program dışı bir akşam çıkacaksa takvimde görünsün — sürpriz çıkışlar hamilelikte iyi karşılanmıyor.</li>
        </ul>
      </div>
      )}
    </>
  )
}
